import {
  Badge,
  Button,
  Card,
  Code,
  Group,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { unwrap } from '../api/client';
import { useOrg } from '../api/org';
import {
  createAgentSession,
  getAgent,
  getAgentFiles,
  listAgentVersions,
  publishAgent,
} from '../api/gen';

/**
 * One agent: its files, its versions, and a way to talk to it.
 *
 * The files tab is read-only, and deliberately. mindvm had a web editor and
 * then reversed it: code enters the system through the sync, so an editor here
 * would be a second way in whose changes a coding tool would not see. What the
 * console shows is what is on the server, and what changes it is a push.
 */
export function AgentPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queries = useQueryClient();
  const headers = useOrg();

  const agent = useQuery({ queryKey: ['agent', id], queryFn: () => unwrap(getAgent({ headers, path: { id } })) });
  const files = useQuery({
    queryKey: ['agent-files', id],
    queryFn: () => unwrap(getAgentFiles({ headers, path: { id } })),
  });
  const versions = useQuery({
    queryKey: ['agent-versions', id],
    queryFn: () => unwrap(listAgentVersions({ headers, path: { id }, query: { limit: 20 } })),
  });

  const publish = useMutation({
    mutationFn: () => unwrap(publishAgent({ headers, path: { id } })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ['agent-versions', id] }),
  });

  const talk = useMutation({
    mutationFn: () => unwrap(createAgentSession({ headers, body: { agentId: id } })),
    onSuccess: (session) => navigate(`/sessions/${session.id}`),
  });

  if (!agent.data) return <Text>Loading…</Text>;
  const gov = (agent.data.governance ?? {}) as Record<string, unknown>;

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={3}>{agent.data.localId}</Title>
          <Text c="dimmed" size="sm">
            {agent.data.model.provider}/{agent.data.model.name}
          </Text>
        </div>
        <Group>
          <Button variant="default" loading={publish.isPending} onClick={() => publish.mutate()}>
            Publish a version
          </Button>
          <Button loading={talk.isPending} onClick={() => talk.mutate()}>
            Open a session
          </Button>
        </Group>
      </Group>

      <Tabs defaultValue="files">
        <Tabs.List>
          <Tabs.Tab value="files">Files</Tabs.Tab>
          <Tabs.Tab value="versions">Versions</Tabs.Tab>
          <Tabs.Tab value="governance">Governance</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="files" pt="md">
          <Text c="dimmed" size="sm" mb="sm">
            What the runtime resolves, byte for byte. Editing happens in the
            folder and arrives by <code>mindvm dev</code>.
          </Text>
          <Stack gap="sm">
            {files.data?.files.map((f) => (
              <Card key={f.path} withBorder>
                <Group justify="space-between" mb="xs">
                  <Text ff="monospace" size="sm">
                    {f.path}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {f.size} bytes
                  </Text>
                </Group>
                <ScrollArea.Autosize mah={320}>
                  <Code block>{f.content}</Code>
                </ScrollArea.Autosize>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="versions" pt="md">
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Version</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Published</Table.Th>
                <Table.Th>By</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {versions.data?.versions.map((v) => (
                <Table.Tr key={v.id}>
                  <Table.Td>{v.versionNumber}</Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" c="dimmed">
                      {v.sourceHash.replace('sha256:', '').slice(0, 12)}
                    </Text>
                  </Table.Td>
                  <Table.Td>{new Date(v.publishedAt).toLocaleString()}</Table.Td>
                  <Table.Td>{v.publishedBy}</Table.Td>
                </Table.Tr>
              ))}
              {versions.data?.versions.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text c="dimmed">Never published.</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Tabs.Panel>

        <Tabs.Panel value="governance" pt="md">
          <Text c="dimmed" size="sm" mb="sm">
            Declared in <code>agent.jsonc</code> and snapshotted onto every
            published version.
          </Text>
          {Object.keys(gov).length === 0 ? (
            <Badge variant="light" color="gray">
              undeclared — treated as minimal risk
            </Badge>
          ) : (
            <Card withBorder>
              <Code block>{JSON.stringify(gov, null, 2)}</Code>
            </Card>
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
